export type UnitSystem = 'metric' | 'imperial';

export type DateStyle = 'short' | 'medium' | 'long' | 'full';

export type WeightUom = 'kg' | 'g' | 't' | 'lb' | 'oz';
export type VolumeUom = 'm3' | 'l' | 'ml' | 'ft3' | 'in3';
export type DimensionUom = 'mm' | 'cm' | 'm' | 'in' | 'ft';

export interface FormattersConfig {
  /** BCP-47 tag or the app's short code (`en`, `tr`). Default `en`. */
  locale?: string;
  /** IANA zone every date/time is shown in; defaults to the browser's. */
  timeZone?: string;
  /** Which unit family measures are shown in. Default `metric`. */
  units?: UnitSystem;
  /** `Intl.NumberFormat` currency display. Default `symbol`. */
  currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
}

export type DateInput = Date | string | number | null | undefined;

export interface PercentOptions {
  /** `fraction` when 0.25 means 25 % (default); `percent` when 25 means 25 %. */
  basis?: 'fraction' | 'percent';
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
}

export interface CurrencyAmount {
  amount: number | null | undefined;
  /** ISO 4217. */
  currency: string;
}

export interface MeasureInput<U extends string> {
  value: number | null | undefined;
  uom: U;
}

export interface QuantityFormatInput {
  value: number | null | undefined;
  /** Shown verbatim after the number (`EA`, `PAL`, `kg`). */
  uom?: string | null;
}

export interface DurationOptions {
  /** What the number is. Default `ms`. */
  unit?: 'ms' | 's';
  /** `short` → `2 h 5 min`; `long` → `2 hours 5 minutes` in the locale. Default `short`. */
  style?: 'short' | 'long';
  /** How many units to show, largest first. Default 2. */
  maxParts?: number;
}

export interface Formatters {
  readonly locale: string;
  readonly timeZone: string | undefined;
  readonly units: UnitSystem;
  /** `Apr 12, 2025` (`medium`), `4/12/25` (`short`), `April 12, 2025` (`long`), `Saturday, April 12, 2025` (`full`). */
  date(value: DateInput, style?: DateStyle): string;
  /** `Apr 12, 2025, 3:04 PM`. */
  dateTime(value: DateInput): string;
  /** `3:04 PM`. */
  time(value: DateInput): string;
  /** `2 hours ago`, `in 3 days`, `yesterday`; the natural phrasing where the locale has one. */
  relative(value: DateInput, now?: DateInput): string;
  /** The full timestamp with its zone, for a `title` behind a relative time. */
  absoluteTitle(value: DateInput): string;
  number(value: number | null | undefined, options?: Intl.NumberFormatOptions): string;
  integer(value: number | null | undefined): string;
  percent(value: number | null | undefined, options?: PercentOptions): string;
  /** `$1,234.50`, `₺1.234,50`. */
  currency(input: CurrencyAmount): string;
  /** `$284K`, `₺1.2M`. */
  currencyCompact(input: CurrencyAmount): string;
  /** Weight in the user's unit family (`kg` or `lb`), with the suffix: `12.5 kg`. */
  weight(input: MeasureInput<WeightUom>): string;
  /** Volume in the user's unit family (`m³` or `ft³`). */
  volume(input: MeasureInput<VolumeUom>): string;
  /** A length in the user's unit family (`cm` or `in`). */
  dimension(input: MeasureInput<DimensionUom>): string;
  /** A count with its unit of measure as given: `1,200 EA`. */
  quantity(input: QuantityFormatInput): string;
  /** `18 s`, `2 h 5 min`; `long` spells the units out in the locale. Always carries a unit. */
  duration(value: number | null | undefined, options?: DurationOptions): string;
  /** `1.2K`, `3.4M`. */
  compact(value: number | null | undefined): string;
  /** `a, b, and c` — the locale's conjunction list. */
  list(items: readonly string[]): string;
}

const SHORT_LOCALES: Record<string, string> = { en: 'en-US', tr: 'tr-TR' };

/** Resolves the app's short codes (`en`, `tr`) to full tags; passes any other tag through. */
export function resolveIntlLocale(locale: string | undefined): string {
  if (!locale) return 'en-US';
  return SHORT_LOCALES[locale] ?? locale;
}

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

const WEIGHT_TO_KG: Record<WeightUom, number> = { kg: 1, g: 0.001, t: 1000, lb: 0.45359237, oz: 0.028349523125 };
const VOLUME_TO_M3: Record<VolumeUom, number> = { m3: 1, l: 0.001, ml: 0.000001, ft3: 0.028316846592, in3: 0.000016387064 };
const LENGTH_TO_CM: Record<DimensionUom, number> = { cm: 1, mm: 0.1, m: 100, in: 2.54, ft: 30.48 };

const KG_PER_LB = 0.45359237;
const M3_PER_FT3 = 0.028316846592;
const CM_PER_IN = 2.54;

const DURATION_UNITS = [
  { unit: 'day', ms: 86_400_000, short: 'd' },
  { unit: 'hour', ms: 3_600_000, short: 'h' },
  { unit: 'minute', ms: 60_000, short: 'min' },
  { unit: 'second', ms: 1_000, short: 's' },
] as const;

/**
 * Builds the formatter set for one viewer: their locale, zone and unit family. Pure `Intl`; every
 * function returns `''` for a missing or unreadable value so a cell never shows `NaN` or `Invalid Date`.
 * Unit symbols for measures are the SI/customary ones (`kg`, `m³`, `in`) rather than locale words,
 * because they are what appears on the documents the numbers are checked against.
 */
export function createFormatters(config: FormattersConfig = {}): Formatters {
  const locale = resolveIntlLocale(config.locale);
  const { timeZone, units = 'metric', currencyDisplay = 'symbol' } = config;
  const cache = new Map<string, Intl.NumberFormat>();
  const numberFormat = (options: Intl.NumberFormatOptions = {}): Intl.NumberFormat => {
    const key = JSON.stringify(options);
    let format = cache.get(key);
    if (!format) {
      format = new Intl.NumberFormat(locale, options);
      cache.set(key, format);
    }
    return format;
  };
  const dateFormat = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, { timeZone, ...options });
  const relativeFormat = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const listFormat = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' });

  const number: Formatters['number'] = (value, options) =>
    isNumber(value) ? numberFormat({ maximumFractionDigits: 3, ...options }).format(value) : '';

  const measure = (value: number | null | undefined, suffix: string, digits = 2) =>
    isNumber(value) ? `${numberFormat({ maximumFractionDigits: digits }).format(value)} ${suffix}` : '';

  return {
    locale,
    timeZone,
    units,
    date: (value, style = 'medium') => {
      const date = toDate(value);
      if (!date) return '';
      // L-06: a calendar date ("2026-09-01") has no zone — shown in the viewer's zone it moved a day west of UTC.
      const calendarDay = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
      return calendarDay ? dateFormat({ dateStyle: style, timeZone: 'UTC' }).format(date) : dateFormat({ dateStyle: style }).format(date);
    },
    dateTime: (value) => {
      const date = toDate(value);
      return date ? dateFormat({ dateStyle: 'medium', timeStyle: 'short' }).format(date) : '';
    },
    time: (value) => {
      const date = toDate(value);
      return date ? dateFormat({ timeStyle: 'short' }).format(date) : '';
    },
    relative: (value, now) => {
      const date = toDate(value);
      if (!date) return '';
      const reference = toDate(now) ?? new Date();
      const seconds = (date.getTime() - reference.getTime()) / 1000;
      const abs = Math.abs(seconds);
      if (abs < 45) return relativeFormat.format(0, 'second');
      if (abs < 3600) return relativeFormat.format(Math.round(seconds / 60), 'minute');
      if (abs < 86_400) return relativeFormat.format(Math.round(seconds / 3600), 'hour');
      if (abs < 7 * 86_400) return relativeFormat.format(Math.round(seconds / 86_400), 'day');
      if (abs < 30 * 86_400) return relativeFormat.format(Math.round(seconds / (7 * 86_400)), 'week');
      if (abs < 365 * 86_400) return relativeFormat.format(Math.round(seconds / (30 * 86_400)), 'month');
      return relativeFormat.format(Math.round(seconds / (365 * 86_400)), 'year');
    },
    absoluteTitle: (value) => {
      const date = toDate(value);
      return date ? dateFormat({ dateStyle: 'full', timeStyle: 'long' }).format(date) : '';
    },
    number,
    integer: (value) => (isNumber(value) ? numberFormat({ maximumFractionDigits: 0 }).format(value) : ''),
    percent: (value, options = {}) => {
      if (!isNumber(value)) return '';
      const fraction = options.basis === 'percent' ? value / 100 : value;
      return numberFormat({
        style: 'percent',
        maximumFractionDigits: options.maximumFractionDigits ?? 1,
        minimumFractionDigits: options.minimumFractionDigits ?? 0,
      }).format(fraction);
    },
    currency: ({ amount, currency }) =>
      isNumber(amount) ? numberFormat({ style: 'currency', currency, currencyDisplay }).format(amount) : '',
    currencyCompact: ({ amount, currency }) =>
      isNumber(amount)
        ? numberFormat({ style: 'currency', currency, currencyDisplay, notation: 'compact', maximumFractionDigits: 1 }).format(amount)
        : '',
    weight: ({ value, uom }) => {
      if (!isNumber(value)) return '';
      const kg = value * WEIGHT_TO_KG[uom];
      return units === 'metric' ? measure(kg, 'kg') : measure(kg / KG_PER_LB, 'lb');
    },
    volume: ({ value, uom }) => {
      if (!isNumber(value)) return '';
      const m3 = value * VOLUME_TO_M3[uom];
      return units === 'metric' ? measure(m3, 'm³', 3) : measure(m3 / M3_PER_FT3, 'ft³', 2);
    },
    dimension: ({ value, uom }) => {
      if (!isNumber(value)) return '';
      const cm = value * LENGTH_TO_CM[uom];
      return units === 'metric' ? measure(cm, 'cm', 1) : measure(cm / CM_PER_IN, 'in', 1);
    },
    quantity: ({ value, uom }) => {
      const text = number(value);
      return text && uom ? `${text} ${uom}` : text;
    },
    duration: (value, options = {}) => {
      if (!isNumber(value)) return '';
      const { unit = 'ms', style = 'short', maxParts = 2 } = options;
      let remaining = Math.max(0, Math.round(unit === 's' ? value * 1000 : value));
      const parts: string[] = [];
      for (const entry of DURATION_UNITS) {
        if (parts.length >= maxParts) break;
        const count = Math.floor(remaining / entry.ms);
        if (count === 0 && !(entry.unit === 'second' && parts.length === 0)) continue;
        remaining -= count * entry.ms;
        parts.push(
          style === 'long'
            ? numberFormat({ style: 'unit', unit: entry.unit, unitDisplay: 'long' }).format(count)
            : `${numberFormat({ maximumFractionDigits: 0 }).format(count)} ${entry.short}`,
        );
      }
      return parts.join(' ');
    },
    compact: (value) => (isNumber(value) ? numberFormat({ notation: 'compact', maximumFractionDigits: 1 }).format(value) : ''),
    list: (items) => listFormat.format(items),
  };
}
